import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import App from './App';
import { getDefaultStyles } from './components/styles/ChatStyles';
import { WidgetConfig, ChatSettings } from './components/types/ChatTypes';
import { detectLanguage, saveLanguageToCookie } from './lib/i18n/languageDetection';

// Function to fetch chat settings from the API
const fetchChatSettings = async (websiteName: string): Promise<ChatSettings | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/website/chat-settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': process.env.REACT_APP_API_KEY || '',
      },
      body: JSON.stringify({
        website_url: websiteName,
      }),
    });

    if (!response.ok) {
      console.error('Failed to fetch chat settings:', response.statusText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching chat settings:', error);
    return null;
  }
};

// Function to generate a random ID of specified length
const generateRandomId = (length: number): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  
  return result;
};

// Create a safe cookie name from website name
const getSafeCookieName = (websiteName: string): string => {
  // Replace any non-alphanumeric characters with underscores and convert to lowercase
  return websiteName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
};

// Function to get or create a user ID cookie
const getUserIdFromCookie = (websiteName: string): string => {
  // Create a website-specific cookie name
  const safeWebsiteName = getSafeCookieName(websiteName);
  const cookieName = `creative_ones_chat_${safeWebsiteName}_user_id`;
  
  try {
    // Check if cookie exists
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(cookieName + '='))
      ?.split('=')[1];
    
    if (cookieValue && cookieValue.length >= 20) {
      return cookieValue;
    }
    
    // Generate a new user ID if no valid cookie found
    const newUserId = generateRandomId(20);
    
    // Set cookie with expiration far in the future (10 years)
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 10);
    
    // Set the cookie with secure attributes
    document.cookie = `${cookieName}=${newUserId}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax; ${window.location.protocol === 'https:' ? 'Secure;' : ''}`;

    return newUserId;
  } catch (error) {
    // Fallback in case of errors with cookies (e.g., in incognito mode)
    console.warn('Error handling cookies:', error);
    return generateRandomId(20); // Return a temporary ID
  }
};

// Get config from the window object or use defaults
const getWidgetConfig = (): WidgetConfig => {
  const userConfig = window.CreativeOnesChatbot?.config;
  
  // Get website name, defaulting to current domain if none provided
  const websiteName = userConfig?.websiteName || window.location.hostname || 'default';
  
  // Get or create user ID from cookie using the website name
  const userId = getUserIdFromCookie(websiteName);
  
  // Detect language using priority order: userConfig -> URL -> cookie -> browser -> default 'ro'
  const detectedLanguage = detectLanguage(userConfig?.langCode, 'ro');
  
  // Save detected language to cookie for future use
  saveLanguageToCookie(detectedLanguage);
  
  if (!userConfig) {
    // Return default config if user config not found
    return {
      userId,
      // websiteName,
      websiteName: websiteName,
      langCode: detectedLanguage,
      style: getDefaultStyles(),
    };
  }
  
  // Merge user config with default styles and make sure we use cookie-based userId
  return {
    ...userConfig,
    userId, // Override with cookie-based user ID
    // websiteName, // Ensure websiteName is set
    websiteName: websiteName,
    langCode: detectedLanguage, // Override with detected language
    style: {
      ...getDefaultStyles(),
      ...userConfig.style,
    }
  };
};

// Create and render widget
const renderWidget = async () => {
  const targetElement = document.getElementById('creative-ones-chatbot');

  if (targetElement) {
    // Get initial config to extract website name
    const preliminaryConfig = getWidgetConfig();
    
    // Fetch chat settings from API
    const chatSettings = await fetchChatSettings(preliminaryConfig.websiteName || window.location.hostname);
    
    // Store chat settings globally
    window.CreativeOnesChatbot = window.CreativeOnesChatbot || {};
    window.CreativeOnesChatbot.chatSettings = chatSettings;
    
    const root = ReactDOM.createRoot(targetElement);
    const config = getWidgetConfig();

    root.render(
      <App config={config} />
    );

    // Return true if rendering was successful
    return true;
  }
  
  // Return false if target element not found
  return false;
};

// Set style function for dynamic style updates
const setStyle = (styleUpdates: Partial<WidgetConfig['style']>) => {
  if (!window.CreativeOnesChatbot || !window.CreativeOnesChatbot.config) {
    // Create config object if it doesn't exist
    window.CreativeOnesChatbot = {
      config: {
        style: styleUpdates
      }
    };
  } else {
    // Merge new style updates with existing styles
    window.CreativeOnesChatbot.config.style = {
      ...window.CreativeOnesChatbot.config.style,
      ...styleUpdates
    };
  }
  
  // Re-render the widget to apply the style updates
  return renderWidget();
};

// Initialize the widget
(async () => {
  await renderWidget();
})();

// Expose public API for the parent application
window.CreativeOnesChatbot = window.CreativeOnesChatbot || {};
window.CreativeOnesChatbot.render = renderWidget;
window.CreativeOnesChatbot.setStyle = setStyle;
